import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Pause,
  Play,
  Copy,
  Trash2,
  Users,
  Send,
  Eye,
  MousePointer,
  MessageSquare,
  Calendar,
  Clock,
  Target,
  User,
  Filter,
  CheckCircle2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { useState } from "react";
import {
  useCampaignQuery,
  useUpdateStatusMutation,
  useDuplicateCampaignMutation,
  useDeleteCampaignMutation,
  useCampaignAnalyticsQuery,
  useCampaignContactsQuery,
  useCampaignEmailsQuery,
  useCampaignActivityQuery,
  type CampaignActivity,
} from "@/hooks/useCampaigns";
import { useRegisterBreadcrumbTitle } from "@/context/BreadcrumbContext";

const TAB_LIST = ["Overview", "Contacts", "Emails", "Details", "Activity"] as const;
type Tab = (typeof TAB_LIST)[number];

const statusBadgeClass = (status: string) => {
  switch (status) {
    case "active":
      return "cdp-badge-green";
    case "paused":
      return "cdp-badge-amber";
    case "draft":
      return "cdp-badge-gray";
    case "completed":
      return "cdp-badge-blue";
    default:
      return "cdp-badge-gray";
  }
};

const formatRelativeTime = (dateStr: string): string => {
  const diff = Math.max(0, Date.now() - new Date(dateStr).getTime());
  const totalMins = Math.floor(diff / 60000);
  const d = Math.floor(totalMins / 1440);
  const h = Math.floor((totalMins % 1440) / 60);
  const m = totalMins % 60;
  if (d > 0) return `${d}d ${h}h ${m}m ago`;
  if (h > 0) return `${h}h ${m}m ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
};

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useCampaignQuery(id);
  const campaign = data?.data as any;

  useRegisterBreadcrumbTitle(id, campaign?.name);

  const updateStatus = useUpdateStatusMutation();
  const duplicate = useDuplicateCampaignMutation();
  const deleteMutation = useDeleteCampaignMutation();

  const [activeTab, setActiveTab] = useState<Tab>("Overview");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [optimisticStatus, setOptimisticStatus] = useState<string | null>(null);
  const [activityPage, setActivityPage] = useState(1);

  const effectiveStatus = optimisticStatus ?? campaign?.status;

  // ── Analytics (loaded for Overview tab; also used in Details tab) ──────────
  const { data: analyticsData, isLoading: analyticsLoading } = useCampaignAnalyticsQuery(
    id,
    true,
  );
  const analytics = analyticsData?.data;

  // ── Contacts tab ──────────────────────────────────────────────────────────
  const { data: contactsData, isLoading: contactsLoading } = useCampaignContactsQuery(
    id,
    activeTab === "Contacts",
  );
  const contacts = contactsData?.data?.contacts ?? [];

  // ── Emails tab ────────────────────────────────────────────────────────────
  const { data: emailsData, isLoading: emailsLoading } = useCampaignEmailsQuery(
    id,
    true,
  );
  const sequences = emailsData?.data?.sequences ?? [];

  // ── Activity tab ──────────────────────────────────────────────────────────
  const {
    data: activityData,
    isLoading: activityLoading,
    isFetching: activityFetching,
    refetch: refetchActivity,
  } = useCampaignActivityQuery(id, activeTab === "Activity", activityPage);
  const activities   = activityData?.data?.activities ?? [];
  const activityMeta = activityData?.data;

  // ── Action handlers ───────────────────────────────────────────────────────
  const handlePause = () => {
    setOptimisticStatus("paused");
    updateStatus.mutate(
      { id: id!, status: "paused" },
      {
        onSuccess: () => toast.success("Campaign paused", { description: `"${campaign.name}" has been paused.` }),
        onError: (e: Error) => {
          setOptimisticStatus(null);
          toast.error("Failed to pause", { description: e.message });
        },
      },
    );
  };

  const handleResume = () => {
    setOptimisticStatus("active");
    updateStatus.mutate(
      { id: id!, status: "active" },
      {
        onSuccess: () => toast.success("Campaign resumed", { description: `"${campaign.name}" is now active.` }),
        onError: (e: Error) => {
          setOptimisticStatus(null);
          toast.error("Failed to resume", { description: e.message });
        },
      },
    );
  };

  const handleDuplicate = () =>
    duplicate.mutate(id!, {
      onSuccess: () => {
        toast.success("Campaign duplicated", { description: `A copy of "${campaign.name}" has been created.` });
        navigate("/app/campaigns");
      },
      onError: (e: Error) => toast.error("Failed to duplicate", { description: e.message }),
    });

  const handleDelete = () =>
    deleteMutation.mutate(id!, {
      onSuccess: () => {
        toast.success("Campaign deleted", { description: `"${campaign.name}" has been permanently deleted.` });
        navigate("/app/campaigns");
      },
      onError: (e: Error) => {
        toast.error("Failed to delete", { description: e.message });
        setShowDeleteConfirm(false);
      },
    });

  const formatSchedule = () => {
    if (!campaign) return "—";
    if (campaign.schedule_type === "immediately") return "Send Immediately";
    if (campaign.scheduled_at) {
      return new Date(campaign.scheduled_at).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return "Scheduled";
  };

  const formatTime = (t: string | null) => {
    if (!t) return "—";
    const [h, m] = t.split(":").map(Number);
    return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
  };

  if (isLoading) {
    return (
      <div className="portal-page cdp-page">
        <div className="cdp-loading">Loading campaign…</div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="portal-page cdp-page">
        <div className="cdp-loading">Campaign not found.</div>
      </div>
    );
  }

  // ── Derived values for Overview ───────────────────────────────────────────
  const sent      = analytics?.sent       ?? 0;
  const delivered = analytics?.delivered  ?? 0;
  const opened    = analytics?.opened     ?? 0;
  const clicked   = analytics?.clicked    ?? 0;
  const seen      = analytics?.seen       ?? 0;
  const replied   = analytics?.replied    ?? 0;
  const bounced   = analytics?.bounced    ?? 0;
  const noAction  = Math.max(0, sent - opened - replied - bounced);

  const metrics = [
    { label: "Sent",      value: sent,      icon: Send,          color: "var(--navy)",  tooltip: "Total emails dispatched in this campaign." },
    { label: "Delivered", value: delivered, icon: CheckCircle2,  color: "var(--sky)",   tooltip: "Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100" },
    { label: "Not Replied Yet", value: opened,    icon: Eye,           color: "var(--amber)", tooltip: "Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100" },
    { label: "Viewed",    value: seen,      icon: MousePointer,  color: "var(--green)", tooltip: "Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100" },
    { label: "Replied",   value: replied,   icon: MessageSquare, color: "var(--sky)",   tooltip: "Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100" },
  ];

  const funnelData = [
    { label: "Delivered",      value: delivered, pct: sent      > 0 ? Math.round((delivered / sent)      * 100) : 0, color: "var(--sky)",      tooltip: "Percentage of sent emails that successfully reached recipient inboxes.\nFormula: Delivered ÷ Total Sent × 100" },
    { label: "Not Replied Yet",value: opened,    pct: delivered > 0 ? Math.round((opened    / delivered) * 100) : 0, color: "var(--amber)",    tooltip: "Percentage of delivered emails that were read but not yet replied to — indicating actively engaged recipients.\nFormula: (Delivered − Replied) ÷ Delivered × 100" },
    { label: "Viewed",         value: seen,      pct: delivered > 0 ? Math.round((seen      / delivered) * 100) : 0, color: "var(--green)",    tooltip: "Percentage of delivered emails that were opened by the recipient.\nFormula: Viewed ÷ Delivered × 100" },
    { label: "Replied",        value: replied,   pct: delivered > 0 ? Math.round((replied   / delivered) * 100) : 0, color: "var(--sky)",      tooltip: "Percentage of delivered emails that received a direct reply from the recipient.\nFormula: Replied ÷ Delivered × 100" },
    { label: "Bounced",        value: bounced,   pct: sent      > 0 ? Math.round((bounced   / sent)      * 100) : 0, color: "var(--red)",      tooltip: "Emails that could not be delivered due to hard or soft bounces." },
  ];

  return (
    <div className="portal-page cdp-page">
      {/* Header */}
      <div className="cdp-header">
        <div className="cdp-header-left">
          <Link to="/app/campaigns" className="cdp-back-link">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <div className="cdp-breadcrumb">
              <Link to="/app/campaigns">Campaigns</Link>
              <span className="cdp-breadcrumb-sep">›</span>
              <span>{campaign.name}</span>
            </div>
            <div className="cdp-title-row">
              <h1 className="cdp-title">{campaign.name}</h1>
              <span className={`cdp-badge ${statusBadgeClass(effectiveStatus)}`}>
                {effectiveStatus === "active" && <span className="cdp-pulse-dot" />}
                {effectiveStatus.charAt(0).toUpperCase() + effectiveStatus.slice(1)}
              </span>
              <span className="cdp-badge cdp-badge-gray cdp-id-badge">{campaign.campaign_id}</span>
            </div>
          </div>
        </div>

        <div className="cdp-header-actions">
          {effectiveStatus === "active" && (
            <button className="btn btn-secondary btn-sm" onClick={handlePause} disabled={updateStatus.isPending}>
              <Pause size={14} /> {updateStatus.isPending ? "Pausing…" : "Pause"}
            </button>
          )}
          {effectiveStatus === "paused" && (
            <button className="btn btn-secondary btn-sm" onClick={handleResume} disabled={updateStatus.isPending}>
              <Play size={14} /> {updateStatus.isPending ? "Resuming…" : "Resume"}
            </button>
          )}
          {effectiveStatus === "draft" && (
            <button className="btn btn-primary btn-sm" onClick={() => navigate(`/app/campaigns/${id}/edit`)}>
              <Send size={14} /> Launch
            </button>
          )}
          <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/app/campaigns/${id}/edit`)}>
            <Edit size={14} /> Edit
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleDuplicate} disabled={duplicate.isPending}>
            <Copy size={14} /> {duplicate.isPending ? "Duplicating…" : "Duplicate"}
          </button>
          <button className="btn btn-secondary btn-sm cdp-delete-btn" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="cdp-tabs">
        {TAB_LIST.map((tab) => (
          <button
            key={tab}
            className={`cdp-tab${activeTab === tab ? " cdp-tab-active" : ""}`}
            onClick={() => {
              setActiveTab(tab);
              if (tab === "Activity") {
                setActivityPage(1);
                refetchActivity();
              }
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Overview tab ── */}
      {activeTab === "Overview" && (
        <>
          {analyticsLoading && !analytics ? (
            <div className="cdp-loading">Loading metrics…</div>
          ) : (
            <>
              <div className="cdp-metrics-grid">
                {metrics.map((m) => (
                  <Tooltip key={m.label}>
                    <TooltipTrigger asChild>
                      <div className="card cdp-metric-card" style={{ borderTop: `3px solid ${m.color}`, cursor: 'default' }}>
                        <m.icon size={20} color={m.color} />
                        <p className="cdp-metric-value">{m.value.toLocaleString()}</p>
                        <p className="cdp-metric-label">{m.label}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                      {m.tooltip}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </div>

              <div className="cdp-charts-grid">
                <div className="card">
                  <h3 className="cdp-card-title">Performance Over Time</h3>
                  <div className="cdp-chart-wrap">
                    {(() => {
                      const ts = analytics?.time_series ?? [];
                      const hasData = ts.some((d) => d.opened > 0 || d.clicked > 0);
                      if (!hasData) {
                        return <p className="cdp-detail-empty" style={{ padding: "2rem 0" }}>No data yet.</p>;
                      }
                      const W = 400, H = 180;
                      const step = ts.length > 1 ? W / (ts.length - 1) : W;
                      const maxOpened  = Math.max(1, ...ts.map((d) => d.opened));
                      const maxClicked = Math.max(1, ...ts.map((d) => d.clicked));
                      const openPts    = ts.map((d, i) => `${i * step},${H - (d.opened  / maxOpened)  * H}`).join(" ");
                      const clickedPts = ts.map((d, i) => `${i * step},${H - (d.clicked / maxClicked) * H}`).join(" ");
                      return (
                        <>
                          <svg viewBox={`0 0 ${W} ${H}`} className="cdp-chart-svg">
                            <polyline fill="none" stroke="var(--amber)" strokeWidth="2" strokeDasharray="5,5" points={openPts} />
                            <polyline fill="none" stroke="var(--green)" strokeWidth="2" points={clickedPts} />
                          </svg>
                          <div className="cdp-chart-legend">
                            <span className="cdp-legend-item"><span className="cdp-legend-line cdp-legend-amber" /> Not Replied Yet</span>
                            <span className="cdp-legend-item"><span className="cdp-legend-line cdp-legend-green" /> Viewed</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="card">
                  <h3 className="cdp-card-title">Conversion Funnel</h3>
                  <div className="cdp-funnel">
                    {funnelData.map((item) => (
                      <div key={item.label}>
                        <div className="cdp-funnel-row">
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <span className="cdp-funnel-label">{item.label}</span>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <HelpCircle size={11} style={{ color: 'var(--gray-400)', cursor: 'help' }} />
                              </TooltipTrigger>
                              <TooltipContent side="right" className="max-w-[240px] text-center whitespace-pre-line leading-snug">
                                {item.tooltip}
                              </TooltipContent>
                            </Tooltip>
                          </span>
                          <span className="cdp-funnel-count">
                            {item.value.toLocaleString()} ({item.pct}%)
                          </span>
                        </div>
                        <div className="cdp-funnel-bar-bg">
                          <div className="cdp-funnel-bar-fill" style={{ width: `${item.pct}%`, background: item.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* ── Email Steps Performance ── */}
              {(() => {
                type StepRow = {
                  stepNum: number;
                  variantLabel: string;
                  multiVariant: boolean;
                  subjectLine: string;
                  sent: number;
                  opened: number;
                  seen: number;
                  clicked: number;
                  replied: number;
                };
                const rows: StepRow[] = [];
                (emailsData?.data?.sequences ?? []).forEach((seq) => {
                  seq.steps.forEach((step) => {
                    const multi = step.variants.length > 1;
                    step.variants.forEach((v) => {
                      rows.push({
                        stepNum: step.step_number,
                        variantLabel: v.variant_label,
                        multiVariant: multi,
                        subjectLine: v.subject_line ?? '(no subject)',
                        sent: v.sent,
                        opened: v.opened,
                        seen: v.seen,
                        clicked: v.clicked,
                        replied: v.replied,
                      });
                    });
                  });
                });
                if (emailsLoading && rows.length === 0) return null;
                return (
                  <div className="card" style={{ marginTop: '1rem' }}>
                    <h3 className="cdp-card-title">Email Steps Performance</h3>
                    {rows.length === 0 ? (
                      <p className="cdp-detail-empty">No step data yet.</p>
                    ) : (
                      <div className="cdp-table-scroll">
                        <table className="cdp-table">
                          <thead>
                            <tr>
                              <th>Step</th>
                              <th>Subject Line</th>
                              <th style={{ textAlign: 'right' }}>Sent</th>
                              <th style={{ textAlign: 'right' }}>Not Reply Rate</th>
                              <th style={{ textAlign: 'right' }}>Viewed Rate</th>
                              <th style={{ textAlign: 'right' }}>Reply Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((row, i) => {
                              const openRate  = row.sent > 0 ? ((row.opened / row.sent) * 100).toFixed(1) : '0.0';
                              const clickRate = row.sent > 0 ? ((row.seen   / row.sent) * 100).toFixed(1) : '0.0';
                              const replyRate = row.sent > 0 ? ((row.replied / row.sent) * 100).toFixed(1) : '0.0';
                              const stepDisplay = row.multiVariant
                                ? `${row.stepNum}${row.variantLabel}`
                                : `${row.stepNum}`;
                              return (
                                <tr key={i}>
                                  <td>{stepDisplay}</td>
                                  <td>{row.subjectLine}</td>
                                  <td style={{ textAlign: 'right' }}>{row.sent.toLocaleString()}</td>
                                  <td style={{ textAlign: 'right' }}>{openRate}%</td>
                                  <td style={{ textAlign: 'right' }}>{clickRate}%</td>
                                  <td style={{ textAlign: 'right' }}>{replyRate}%</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </>
      )}

      {/* ── Contacts tab ── */}
      {activeTab === "Contacts" && (
        <div className="card cdp-contacts-card">
          {contactsLoading ? (
            <div className="cdp-loading">Loading contacts…</div>
          ) : contacts.length === 0 ? (
            <div className="cdp-loading">No contacts have received emails from this campaign yet.</div>
          ) : (
            <div className="cdp-table-scroll">
              <table className="cdp-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Status</th>
                    <th>Not Replied Yet</th>
                    <th>Replied</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact: any, i: number) => (
                    <tr key={contact.email ?? i}>
                      <td>
                        <div className="cdp-lead-name-cell">
                          <div className="cdp-lead-avatar">
                            {((contact.name as string) || "UN")
                              .split(" ")
                              .map((n: string) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </div>
                          {contact.lead_id ? (
                            <Link to={`/app/leads/${contact.lead_id}`} className="cdp-lead-name-link">
                              {contact.name || contact.email}
                            </Link>
                          ) : (
                            <span className="cdp-lead-name-link">{contact.name || contact.email}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="cdp-table-secondary">
                          {contact.company || "—"}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const s = contact.replied
                            ? { label: "Replied",            color: "#4CAF50", bg: "#E8F5E9" }
                            : contact.opened
                            ? { label: "Seen (Not Replied)", color: "#F5A623", bg: "#FFF8E1" }
                            : { label: "Waiting Reply",      color: "#E53935", bg: "#FFEBEE" };
                          return (
                            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600, color: s.color, background: s.bg }}>
                              {s.label}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        <span className="cdp-table-secondary">
                          {contact.opened_at ? formatRelativeTime(contact.opened_at) : "—"}
                        </span>
                      </td>
                      <td>
                        <span className="cdp-table-secondary">
                          {contact.replied_at ? formatRelativeTime(contact.replied_at) : "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Emails tab ── */}
      {activeTab === "Emails" && (
        <div className="cdp-emails-list">
          {emailsLoading ? (
            <div className="cdp-loading">Loading emails…</div>
          ) : sequences.length === 0 ? (
            <div className="card">
              <p className="cdp-detail-empty">No email sequences linked to this campaign yet.</p>
            </div>
          ) : (
            sequences.map((seq: any) =>
              seq.steps.map((step: any) =>
                step.variants.map((variant: any) => (
                  <div key={variant.id} className="card cdp-email-card">
                    <div className="cdp-email-card-header">
                      <div className="cdp-step-badge">{step.step_number}</div>
                      <div>
                        <h4 className="cdp-email-step">
                          {step.step_label}
                          {step.variants.length > 1 ? ` – Variant ${variant.variant_label.toUpperCase()}` : ""}
                        </h4>
                        <p className="cdp-email-subject">Subject: {variant.subject_line ?? "—"}</p>
                        {seq.segment_name && (
                          <p className="cdp-table-secondary" style={{ marginTop: "2px" }}>Segment: {seq.segment_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="cdp-email-stats">
                      <span>
                        <strong>{variant.sent.toLocaleString()}</strong>{" "}
                        <span className="cdp-table-secondary">sent</span>
                      </span>
                      <span>
                        <strong>{variant.opened.toLocaleString()}</strong>{" "}
                        <span className="cdp-table-secondary">not replied yet</span>
                      </span>
                      <span>
                        <strong>{variant.replied}</strong>{" "}
                        <span className="cdp-table-secondary">replied</span>
                      </span>
                    </div>
                  </div>
                )),
              ),
            )
          )}
        </div>
      )}

      {/* ── Details tab ── */}
      {activeTab === "Details" && (
        <div className="cdp-details-grid">
          <div className="card cdp-detail-card">
            <div className="cdp-detail-card-header">
              <Users size={16} color="var(--sky)" />
              <h3 className="cdp-detail-card-title">Audience</h3>
            </div>
            <div className="cdp-detail-rows">
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Segments</span>
                <span className="cdp-detail-value">
                  {analytics ? analytics.segment_count.toLocaleString() : analyticsLoading ? "…" : "—"}
                </span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Leads</span>
                <span className="cdp-detail-value">
                  {analytics ? analytics.leads_count.toLocaleString() : analyticsLoading ? "…" : "—"}
                </span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Enrolled</span>
                <span className="cdp-detail-value">
                  {analytics ? analytics.enrolled.toLocaleString() : analyticsLoading ? "…" : "—"}
                </span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Contacted</span>
                <span className="cdp-detail-value">
                  {analytics ? analytics.sent.toLocaleString() : analyticsLoading ? "…" : "—"}
                </span>
              </div>
              {campaign.additional_filters?.length > 0 && (
                <div className="cdp-detail-row">
                  <span className="cdp-detail-label">
                    <Filter size={12} className="cdp-inline-icon" />
                    Filters
                  </span>
                  <span className="cdp-detail-value">{campaign.additional_filters.length} active</span>
                </div>
              )}
            </div>
          </div>

          <div className="card cdp-detail-card">
            <div className="cdp-detail-card-header">
              <Target size={16} color="var(--sky)" />
              <h3 className="cdp-detail-card-title">Campaign</h3>
            </div>
            <div className="cdp-detail-rows">
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Goal</span>
                <span className="cdp-detail-value">{campaign.goal?.name ?? "—"}</span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Created</span>
                <span className="cdp-detail-value">
                  {new Date(campaign.created_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Updated</span>
                <span className="cdp-detail-value">
                  {new Date(campaign.updated_at).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </span>
              </div>
            </div>
          </div>

          <div className="card cdp-detail-card">
            <div className="cdp-detail-card-header">
              <User size={16} color="var(--sky)" />
              <h3 className="cdp-detail-card-title">Sender</h3>
            </div>
            {campaign.sender_profile ? (
              <div className="cdp-sender-row">
                <div className="cdp-sender-avatar">
                  {campaign.sender_profile.from_name
                    .split(" ")
                    .map((n: string) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
                <div>
                  <p className="cdp-sender-name">{campaign.sender_profile.from_name}</p>
                  <p className="cdp-sender-email">{campaign.sender_profile.from_email}</p>
                </div>
              </div>
            ) : (
              <p className="cdp-detail-empty">No sender assigned</p>
            )}
          </div>

          <div className="card cdp-detail-card">
            <div className="cdp-detail-card-header">
              <Calendar size={16} color="var(--sky)" />
              <h3 className="cdp-detail-card-title">Schedule</h3>
            </div>
            <div className="cdp-detail-rows">
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Type</span>
                <span className="cdp-detail-value">{formatSchedule()}</span>
              </div>
              {campaign.timezone && (
                <div className="cdp-detail-row">
                  <span className="cdp-detail-label">Timezone</span>
                  <span className="cdp-detail-value">{campaign.timezone}</span>
                </div>
              )}
            </div>
          </div>

          <div className="card cdp-detail-card">
            <div className="cdp-detail-card-header">
              <Clock size={16} color="var(--sky)" />
              <h3 className="cdp-detail-card-title">Sending Window</h3>
            </div>
            <div className="cdp-detail-rows">
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Start</span>
                <span className="cdp-detail-value">{formatTime(campaign.sending_hours_start)}</span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">End</span>
                <span className="cdp-detail-value">{formatTime(campaign.sending_hours_end)}</span>
              </div>
              <div className="cdp-detail-row">
                <span className="cdp-detail-label">Daily Limit</span>
                <span className="cdp-detail-value">
                  {campaign.daily_limit ? `${campaign.daily_limit} emails/day` : "—"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Activity tab ── */}
      {activeTab === "Activity" && (
        <div className="card">
          {/* Header row */}
          <div className="cdp-activity-header">
            <div>
              <h3 className="cdp-card-title" style={{ marginBottom: 0 }}>Email Activity</h3>
              {activityMeta && (
                <p className="cdp-table-secondary" style={{ marginTop: 2 }}>
                  {activityMeta.total === 0
                    ? "No events"
                    : `${(activityPage - 1) * activityMeta.per_page + 1}–${Math.min(activityPage * activityMeta.per_page, activityMeta.total)} of ${activityMeta.total.toLocaleString()} events`}
                </p>
              )}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => { setActivityPage(1); refetchActivity(); }}
              disabled={activityFetching}
              title="Refresh activity"
            >
              <RefreshCw size={13} className={activityFetching ? "cdp-spin" : ""} />
              {activityFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {activityLoading ? (
            <div className="cdp-loading">Loading activity…</div>
          ) : activities.length === 0 ? (
            <p className="cdp-detail-empty cdp-activity-empty">No activity recorded yet.</p>
          ) : (
            <>
              <ul className="cdp-activity-list">
                {activities.map((act: CampaignActivity, i: number) => (
                  <li key={`${act.type}_${act.occurred_at}_${i}`} className="cdp-activity-item">
                    <span className="cdp-activity-dot" />
                    <span className="cdp-activity-text">
                      {act.type === "sent" ? (
                        <><strong>{act.step_label}</strong>{" sent to "}<strong>{(act.contact_count ?? 0).toLocaleString()} contacts</strong></>
                      ) : act.type === "replied" ? (
                        <><strong>{act.contact_name ?? "Unknown"}</strong>{" replied to "}<strong>{act.step_label}</strong></>
                      ) : act.type === "opened" ? (
                        <><strong>{act.contact_name ?? "Unknown"}</strong>{" opened "}<strong>{act.step_label}</strong></>
                      ) : (
                        <><strong>{act.contact_name ?? "Unknown"}</strong>{" message bounced"}</>
                      )}
                    </span>
                    <span className="cdp-activity-time"><Clock size={12} />{formatRelativeTime(act.occurred_at)}</span>
                  </li>
                ))}
              </ul>

              {/* Pagination */}
              {activityMeta && activityMeta.last_page > 1 && (
                <div className="cdp-activity-pagination">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                    disabled={activityPage <= 1 || activityFetching}
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>
                  <span className="cdp-activity-page-info">
                    Page {activityMeta.page} of {activityMeta.last_page}
                  </span>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setActivityPage((p) => Math.min(activityMeta.last_page, p + 1))}
                    disabled={activityPage >= activityMeta.last_page || activityFetching}
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Delete confirm */}
      {showDeleteConfirm && (
        <div className="cdp-overlay">
          <div className="cdp-confirm-dialog">
            <h3 className="cdp-confirm-title">Delete Campaign</h3>
            <p className="cdp-confirm-text">
              Are you sure you want to delete <strong>{campaign.name}</strong>? This action cannot be undone.
            </p>
            <div className="cdp-confirm-actions">
              <button className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary cdp-delete-confirm-btn"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
